
import api from './api';

export interface SubscriptionPlan {
    id: string;
    name: string;
    slug: string;
    price_monthly: string;
    features: string[] | null;
    limits: Record<string, number> | null;
    is_active: boolean;
}

export interface RestaurantSubscription {
    id: string;
    status: string;
    created_at: string;
    subscription_plans: SubscriptionPlan | null;
}

export interface Restaurant {
    id: string;
    name: string;
    slug: string;
    subdomain?: string;
    status: string;
    is_featured?: boolean;
    city: string;
    currency?: string;
    language?: string;
    created_at: string;
    license_expires_at?: string | null;
    restaurant_subscriptions?: RestaurantSubscription[];
    _count?: {
        payment_methods: number;
        warehouses: number;
        point_of_sales: number;
    };
}

export interface RestaurantActivity {
    events: { type: string; label: string; date: string }[];
    local_server_last_sync: { at: string; source: 'redis' | 'push' } | null;
    local_server_version: { version: string; os: string; at: string } | null;
}

export interface RestaurantAdminDetail extends Restaurant {
    email?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    postal_code?: string;
    country?: string;
    website?: string;
    timezone?: string;
    updated_at?: string;
    partners?: {
        id: string;
        users: {
            id: string;
            email: string;
            role: string;
            status: string;
        } | null;
    } | null;
}

export interface CreateRestaurantData {
    name: string;
    description?: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    postal_code: string;
    country: string;
    phone?: string;
    /** Email de contact du restaurant (obligatoire) */
    email: string;
    website?: string;
    /** Email de connexion du manager (obligatoire à la création) */
    manager_email?: string;
    manager_password?: string;
    manager_first_name?: string;
    manager_last_name?: string;
    plan_id?: number;
    tags: string[];
    is_franchise?: boolean;
    latitude?: number;
    services?: { type: string; enabled: boolean; settings?: any }[];
    opening_hours?: { day_of_week: number; is_open: boolean; open_time?: string; close_time?: string; service_period?: string }[];

    // Tunisia Compliance
    manager_name?: string;
    tax_id?: string;
    store_id?: string;
    commercial_register?: string;
    stamp_duty?: number;
    stamp_duty_enabled?: boolean;
    pin_lock_enabled?: boolean;
    pin_lock_seconds?: number;
    show_order_note?: boolean;
    show_product_images?: boolean;
    receipt_print_mode?: 'always' | 'never' | 'ask';

    logo_url?: string;
    currency?: string;
    language?: string;
    timezone?: string;
    status?: 'active' | 'inactive' | 'suspended';
    delivery_radius_km?: string | number;
    base_delivery_fee?: string | number;
}

export const restaurantService = {
    getAll: async (): Promise<{ status: string; data: Restaurant[] }> => {
        const response = await api.get('/restaurants');
        return response.data;
    },

    getAllAdmin: async (): Promise<{ status: string; data: Restaurant[] }> => {
        const response = await api.get('/restaurants/all');
        return response.data;
    },

    getOne: async (id: string) => {
        const response = await api.get(`/restaurants/${id}`);
        return response.data;
    },

    create: async (data: CreateRestaurantData) => {
        const response = await api.post('/restaurants', data);
        return response.data;
    },

    // is_featured et license_expires_at ne sont pas des champs de création : seul le
    // super_admin peut les modifier (liste blanche dans backend restaurant.service.ts).
    update: async (id: string, data: Partial<CreateRestaurantData> & { is_featured?: boolean; license_expires_at?: string | null }) => {
        const response = await api.patch(`/restaurants/${id}`, data);
        return response.data;
    },

    async sendVerificationEmail(id: string) {
        return api.post(`/restaurants/${id}/send-verification-email`);
    },

    async getTaxes(country: string = 'Tunisia') {
        return api.get(`/restaurants/config/taxes?country=${country}`);
    },

    async getBySubdomain(subdomain: string) {
        const response = await api.get(`/restaurants/lookup/${subdomain}`);
        return response.data;
    },

    async getByIdPublic(id: string) {
        const response = await api.get(`/restaurants/lookup/id/${id}`);
        return response.data;
    },

    async getAdminDetail(id: string): Promise<{ status: string; data: { restaurant: RestaurantAdminDetail } }> {
        const response = await api.get(`/restaurants/${id}/admin-detail`);
        return response.data;
    },

    async getActivity(id: string): Promise<{ status: string; data: RestaurantActivity }> {
        const response = await api.get(`/restaurants/${id}/activity`);
        return response.data;
    },

    async reprovision(id: string) {
        const response = await api.post(`/restaurants/${id}/reprovision`);
        return response.data;
    }
};
